import { useState } from 'react';
import ApprovalObjectLink from './ApprovalObjectLink';
import { useApprovalAdminAccess } from './ApprovalAccess';
import { useSelector } from 'react-redux';
import { Check, ChevronRight, Copy, Layers, MoreHorizontal, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/Textarea';
import { SimpleDialog } from '@/components/ui/SimpleDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/Dropdown';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/utils/cn';
import { OBJECT_TYPE_LABELS, REQUEST_SOURCE_LABELS, STATUS_LABELS } from '../constants';
import type { ApprovalEventType, ApprovalRequest } from '../types';
import {
  useApproveApproval,
  useCommentApproval,
  useRejectApproval,
  useSubmitApproval,
} from '../hooks/useApprovals';
import { formatDateTime, initialsOf, relativeTime, shortId, STATUS_VISUAL } from '../helpers';
import CompareDialog from './compare/CompareDialog';

// České popisky typů událostí v timeline.
const EVENT_TYPE_LABELS: Record<ApprovalEventType, string> = {
  created: 'Vytvořeno',
  edited: 'Upraveno',
  submitted: 'Odesláno ke schválení',
  approved: 'Schváleno',
  rejected: 'Zamítnuto',
  commented: 'Komentář',
};

const EVENT_DOT: Record<ApprovalEventType, string> = {
  created: 'bg-neutral-300',
  edited: 'bg-neutral-300',
  submitted: 'bg-amber-500',
  approved: 'bg-emerald-500',
  rejected: 'bg-red-500',
  commented: 'bg-primary-400',
};

interface ApprovalDetailProps {
  request?: ApprovalRequest;
  loading?: boolean;
}

const MetaCell = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div className="text-[10.5px] font-medium uppercase tracking-wider text-neutral-500">
      {label}
    </div>
    <div className="text-[13px] text-neutral-900 mt-0.5">{children}</div>
  </div>
);

const PersonInline = ({ name }: { name?: string }) => {
  if (!name) return <span className="text-neutral-400">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-5 w-5 rounded-full bg-primary-100 text-primary-700 text-[9.5px] font-semibold inline-flex items-center justify-center">
        {initialsOf(name)}
      </span>
      {name}
    </span>
  );
};

const ApprovalDetail = ({ request, loading }: ApprovalDetailProps) => {
  const employeeId = useSelector((state: any) => state.auth.current_employee?.id);
  const isAdmin = useApprovalAdminAccess();

  const [tab, setTab] = useState('prehled');
  const [compareOpen, setCompareOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [newComment, setNewComment] = useState('');

  const submit = useSubmitApproval();
  const approve = useApproveApproval();
  const reject = useRejectApproval();
  const comment = useCommentApproval();

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <SpinnerOverlay />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex-1 flex items-center justify-center text-center px-8">
        <div>
          <div className="mx-auto h-10 w-10 rounded-full bg-neutral-100 flex items-center justify-center mb-3">
            <Check size={17} className="text-neutral-400" />
          </div>
          <div className="text-sm font-medium text-neutral-800">Vyberte žádost</div>
          <div className="text-[12.5px] text-neutral-500 mt-1">Detail se zobrazí zde.</div>
        </div>
      </div>
    );
  }

  const status = request.status;
  const visual = STATUS_VISUAL[status];
  const isAuthor = String(request.submitted_by_id) === String(employeeId);
  const canSubmit = (status === 'draft' || status === 'rejected') && (isAuthor || isAdmin);
  // Backend authorization: pending + current employee may decide (admin/approver).
  const canDecide = request.can_decide === true;

  const typeLabel = OBJECT_TYPE_LABELS[request.object_type];
  const sourceLabel = REQUEST_SOURCE_LABELS[request.request_source] ?? request.request_source;
  const requesterName = request.submitted_by_name;
  const deciderName = request.decided_by_name ?? undefined;

  const events = request.events ?? [];
  const commentEvents = events.filter((e) => e.comment && e.comment.trim());

  const handleReject = () => {
    const trimmed = rejectComment.trim();
    if (!trimmed) return;
    reject.mutate(
      { id: request.id, comment: trimmed },
      {
        onSuccess: () => {
          setRejectOpen(false);
          setRejectComment('');
        },
      }
    );
  };

  const handleAddComment = () => {
    const trimmed = newComment.trim();
    if (!trimmed) return;
    comment.mutate(
      { id: request.id, comment: trimmed },
      {
        onSuccess: () => setNewComment(''),
      }
    );
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col bg-white">
      {/* hlavička detailu */}
      <div className="px-6 pt-4 pb-3 border-b border-neutral-150">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[11px] text-neutral-500">
              <span>{typeLabel}</span>
              <ChevronRight size={10} className="text-neutral-300" />
              <span className="font-mono">{shortId(request.object_id)}</span>
            </div>
            <h1 className="mt-1 text-[17px] font-semibold tracking-tight text-neutral-900 leading-snug">
              {typeLabel}
            </h1>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge variant={visual.badge} size="lg">
              {STATUS_LABELS[status]}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="h-8 w-8 inline-flex items-center justify-center rounded border border-neutral-200 text-neutral-500 hover:bg-neutral-50"
                  data-testid="approval-detail-actions-btn"
                >
                  <MoreHorizontal size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() =>
                    navigator.clipboard?.writeText(String(request.id)).catch(() => undefined)
                  }
                >
                  <Copy size={13} />
                  Kopírovat ID
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <Users size={13} />
                  Přidat schvalovatele
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* akce */}
        {(canSubmit || canDecide) && (
          <div className="mt-3 flex items-center gap-2">
            {canSubmit && (
              <Button
                onClick={() => submit.mutate(request.id)}
                disabled={submit.isLoading}
                data-testid="approval-submit-btn"
              >
                <Check size={13} strokeWidth={2.5} />
                Odeslat ke schválení
              </Button>
            )}
            {canDecide && (
              <>
                <Button
                  onClick={() => approve.mutate(request.id)}
                  disabled={approve.isLoading}
                  data-testid="approval-approve-btn"
                >
                  <Check size={13} strokeWidth={2.5} />
                  Schválit
                </Button>
                <Button
                  variant="ghost"
                  className="text-danger hover:bg-red-50"
                  onClick={() => setRejectOpen(true)}
                  data-testid="approval-reject-btn"
                >
                  <X size={13} />
                  Zamítnout
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCompareOpen(true)}
                  data-testid="compare-open-btn"
                >
                  <Layers size={13} />
                  Porovnat verze
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* meta */}
      <div className="px-6 py-3.5 border-b border-neutral-150 bg-neutral-25 grid grid-cols-4 gap-6">
        <MetaCell label="Žadatel">
          <PersonInline name={requesterName} />
        </MetaCell>
        <MetaCell label="Podáno">
          {formatDateTime(request.created_on)}{' '}
          <span className="text-neutral-400">({relativeTime(request.created_on)})</span>
        </MetaCell>
        <MetaCell label="Zdroj">{sourceLabel}</MetaCell>
        <MetaCell label="Rozhodl">
          {deciderName ? (
            <PersonInline name={deciderName} />
          ) : (
            <span className="text-neutral-400">—</span>
          )}
        </MetaCell>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
        <div className="px-6 border-b border-neutral-150">
          <TabsList className="border-b-0">
            <TabsTrigger value="prehled" data-testid="approval-tab-overview">
              Přehled
            </TabsTrigger>
            <TabsTrigger value="historie" data-testid="approval-tab-history">
              Historie
            </TabsTrigger>
            <TabsTrigger value="komentare" data-testid="approval-tab-comments">
              Komentáře{commentEvents.length ? ` (${commentEvents.length})` : ''}
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <TabsContent value="prehled" className="mt-0">
            <div className="max-w-3xl space-y-6">
              {canDecide && (
                <div className="rounded border border-neutral-200 bg-neutral-25 px-4 py-3 flex items-center gap-3">
                  <Layers size={16} className="text-neutral-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-neutral-900">Porovnání verzí</div>
                    <div className="text-[12px] text-neutral-500">
                      Zobrazte, co se oproti předchozí schválené verzi změnilo.
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCompareOpen(true)}
                    data-testid="compare-overview-open-btn"
                  >
                    Otevřít porovnání
                  </Button>
                </div>
              )}
              {status === 'rejected' && request.rejection_comment && (
                <div className="rounded border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {request.rejection_comment}
                </div>
              )}
              <div className="divide-y divide-neutral-100">
                {[
                  { label: 'Typ entity', value: typeLabel },
                  {
                    label: 'Položka',
                    value: <ApprovalObjectLink request={request} />,
                  },
                  { label: 'Zdroj', value: sourceLabel },
                  { label: 'Stav', value: STATUS_LABELS[status] },
                ].map((it) => (
                  <div
                    key={it.label}
                    className="grid gap-4 items-baseline py-2.5"
                    style={{ gridTemplateColumns: '160px 1fr' }}
                  >
                    <div className="text-sm text-neutral-500 truncate">{it.label}</div>
                    <div className="text-sm text-neutral-900">{it.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="historie" className="mt-0">
            {events.length === 0 ? (
              <div className="text-sm text-neutral-400">Žádné události</div>
            ) : (
              <div className="max-w-2xl">
                {events.map((event, i) => {
                  const actor = event.actor_name;
                  const last = i === events.length - 1;
                  return (
                    <div key={event.id} className="relative pl-6 pb-4">
                      {!last && (
                        <span className="absolute left-[5.5px] top-4 bottom-0 w-px bg-neutral-150" />
                      )}
                      <span
                        className={cn(
                          'absolute left-0 top-1 h-3 w-3 rounded-full border-2 border-white',
                          EVENT_DOT[event.event_type] ?? 'bg-neutral-300'
                        )}
                      />
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[13px] font-medium text-neutral-900">
                          {EVENT_TYPE_LABELS[event.event_type] ?? event.event_type}
                        </span>
                        <span className="text-[11px] text-neutral-400 font-mono">
                          {formatDateTime(event.created_on)}
                        </span>
                        {actor && <span className="text-[11px] text-neutral-500">· {actor}</span>}
                      </div>
                      {event.comment && (
                        <div className="text-[12.5px] text-neutral-600 mt-0.5 leading-relaxed">
                          {event.comment}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="komentare" className="mt-0">
            <div className="max-w-2xl space-y-4">
              {commentEvents.length === 0 && !request.rejection_comment ? (
                <div className="text-[13px] text-neutral-500">Zatím žádné komentáře.</div>
              ) : (
                <>
                  {commentEvents.map((event) => {
                    const actor = event.actor_name;
                    return (
                      <div key={event.id} className="flex gap-3">
                        <span className="h-7 w-7 rounded-full bg-neutral-100 text-neutral-600 text-[10px] font-semibold inline-flex items-center justify-center shrink-0">
                          {initialsOf(actor)}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="text-[13px] font-medium text-neutral-900">
                              {actor ?? '—'}
                            </span>
                            <span className="text-[11px] text-neutral-400 font-mono">
                              {formatDateTime(event.created_on)}
                            </span>
                          </div>
                          <p className="text-[13px] text-neutral-700 mt-0.5">{event.comment}</p>
                        </div>
                      </div>
                    );
                  })}
                  {request.rejection_comment &&
                    !commentEvents.some((e) => e.comment === request.rejection_comment) && (
                      <div className="flex gap-3">
                        <span className="h-7 w-7 rounded-full bg-red-50 text-red-600 text-[10px] font-semibold inline-flex items-center justify-center shrink-0">
                          <X size={12} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium text-neutral-900">
                            Důvod zamítnutí
                          </div>
                          <p className="text-[13px] text-neutral-700 mt-0.5">
                            {request.rejection_comment}
                          </p>
                        </div>
                      </div>
                    )}
                </>
              )}

              {status !== 'approved' && (
                <div className="pt-2 border-t border-neutral-100 space-y-2">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Napište komentář…"
                    data-testid="approval-comment-input"
                  />
                  <Button
                    size="sm"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || comment.isLoading}
                    data-testid="approval-comment-submit-btn"
                  >
                    Odeslat komentář
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </div>
      </Tabs>

      <SimpleDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Zamítnout žádost"
        okText="Zamítnout"
        confirmLoading={reject.isLoading}
        onCancel={() => {
          setRejectOpen(false);
          setRejectComment('');
        }}
        onOk={handleReject}
      >
        <div className="space-y-2">
          <label className="text-sm text-neutral-700">Důvod zamítnutí</label>
          <Textarea
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            placeholder="Uveďte důvod zamítnutí…"
            data-testid="approval-reject-comment-input"
          />
        </div>
      </SimpleDialog>

      <CompareDialog
        key={request.id}
        open={compareOpen}
        onOpenChange={setCompareOpen}
        request={request}
        canDecide={canDecide}
        approveLoading={approve.isLoading}
        onApprove={() => approve.mutate(request.id, { onSuccess: () => setCompareOpen(false) })}
        onReject={() => {
          setCompareOpen(false);
          setRejectOpen(true);
        }}
      />
    </div>
  );
};

export default ApprovalDetail;
